import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { sameOriginImageURL } from "@shared/helpers/constants";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FC, useEffect, useMemo, useState } from "react";
import { getErrorMessage } from "../../pages/auth/Auth";
import { useAlert } from "../../contexts/AlertProvider";
import { OrganizationBackgroundImagesGallery } from "./OrganizationBackgroundImagesGallery";
import {
  deleteOrganizationBackgroundImage,
  fetchImageUsage,
  organizationBackgroundImagesQueryKey,
} from "./imageGalleryApi";
import { useOrganizationBackgroundImages } from "./useOrganizationBackgroundImages";

type Props = {
  open: boolean;
  imageId: string | null;
  organizationId: string | undefined;
  onClose: () => void;
  onDeleted?: () => void;
};

function getReplacementWarning(usage: {
  opportunities: { id: string }[];
  isOrganizationDefault: boolean;
  isOrganizationImage?: boolean;
}): string | null {
  if (usage.opportunities.length > 0) {
    return `התמונה משמשת ב-${usage.opportunities.length} התנדבויות. יש לבחור תמונת החלפה לפני המחיקה.`;
  }
  if (usage.isOrganizationDefault) {
    return "התמונה מוגדרת כברירת מחדל של הארגון. יש לבחור תמונת החלפה לפני המחיקה.";
  }
  if (usage.isOrganizationImage) {
    return "התמונה משמשת כתמונת הארגון. יש לבחור תמונת החלפה לפני המחיקה.";
  }
  return null;
}

export const DeleteBackgroundImageDialog: FC<Props> = ({
  open,
  imageId,
  organizationId,
  onClose,
  onDeleted,
}) => {
  const queryClient = useQueryClient();
  const { setAlertText, setSeverity } = useAlert();
  const [replacementImageId, setReplacementImageId] = useState<string | null>(
    null,
  );

  const {
    data: usage,
    isLoading: isUsageLoading,
    isError: isUsageError,
    error: usageError,
  } = useQuery({
    queryKey: ["image-usage", imageId],
    queryFn: () => fetchImageUsage(imageId!),
    enabled: open && !!imageId,
  });

  const { data: backgroundImages } = useOrganizationBackgroundImages(
    organizationId,
    open && !!usage?.requiresReplacement,
  );

  const replacementOptions = useMemo(
    () => (backgroundImages ?? []).filter((img) => img.imageId !== imageId),
    [backgroundImages, imageId],
  );

  const replacementWarning = usage ? getReplacementWarning(usage) : null;

  useEffect(() => {
    if (!open) {
      setReplacementImageId(null);
    }
  }, [open, imageId]);

  useEffect(() => {
    if (
      replacementOptions.length === 1 &&
      replacementImageId !== replacementOptions[0]!.imageId
    ) {
      setReplacementImageId(replacementOptions[0]!.imageId);
    }
  }, [replacementOptions, replacementImageId]);

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!imageId) return;
      await deleteOrganizationBackgroundImage({
        imageId,
        ...(usage?.requiresReplacement && replacementImageId
          ? { replacementImageId }
          : {}),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: organizationBackgroundImagesQueryKey(organizationId),
      });
      setAlertText("התמונה נמחקה בהצלחה");
      setSeverity("success");
      onDeleted?.();
      onClose();
    },
    onError: (error) => {
      setAlertText(getErrorMessage(error));
      setSeverity("error");
    },
  });

  const canConfirmDelete =
    !!imageId &&
    !!usage &&
    usage.canDelete &&
    (!usage.requiresReplacement ||
      (replacementOptions.length > 0 && !!replacementImageId));

  const hasNoReplacementOptions =
    !!usage?.requiresReplacement && replacementOptions.length === 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      dir="rtl"
      sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
    >
      <DialogTitle>מחיקת תמונת רקע</DialogTitle>
      <DialogContent>
        {isUsageLoading ? (
          <Stack alignItems="center" py={3}>
            <CircularProgress size={28} />
          </Stack>
        ) : isUsageError ? (
          <Alert severity="error">{getErrorMessage(usageError)}</Alert>
        ) : usage && !usage.canDelete ? (
          <Alert severity="error">
            לא ניתן למחוק את תמונת ברירת המחדל של המערכת.
          </Alert>
        ) : usage ? (
          <Stack gap={2}>
            {imageId && (
              <Box
                component="img"
                src={sameOriginImageURL(imageId)}
                alt="תמונה למחיקה"
                sx={{
                  width: "100%",
                  maxHeight: 180,
                  objectFit: "cover",
                  borderRadius: 1,
                }}
              />
            )}
            {replacementWarning && (
              <Alert severity="warning">{replacementWarning}</Alert>
            )}
            {!usage.requiresReplacement && (
              <Typography variant="body2" color="text.secondary">
                המחיקה תסיר את התמונה מהגלריה. משימות סגורות או בארכיון לא חוסמות את המחיקה.
              </Typography>
            )}
            {usage.requiresReplacement && (
              <Stack gap={1}>
                <Typography variant="subtitle2">בחר תמונת החלפה:</Typography>
                {hasNoReplacementOptions ? (
                  <Alert severity="error">
                    אין תמונות החלפה זמינות בגלריה. יש להעלות תמונה חדשה לפני
                    המחיקה.
                  </Alert>
                ) : (
                  <OrganizationBackgroundImagesGallery
                    images={replacementOptions}
                    selectedImageId={replacementImageId ?? undefined}
                    onSelectImage={setReplacementImageId}
                  />
                )}
              </Stack>
            )}
          </Stack>
        ) : (
          <Alert severity="error">לא ניתן לטעון את פרטי המחיקה.</Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={deleteMutation.isPending}>
          ביטול
        </Button>
        <Button
          color="error"
          variant="contained"
          disabled={!canConfirmDelete || deleteMutation.isPending}
          onClick={() => deleteMutation.mutate()}
        >
          {deleteMutation.isPending ? <CircularProgress size={22} /> : "מחיקה"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
